// KPIs › Scorecard's cell parts (KPIS-MODE, founder-approved 2026-10-22). `GridScoreCell` is one
// period of a KPI row: an editable cell is a button with a hover pencil and the focus ring (the
// grid types into it); a read-only one says why on hover or focus (`cellBlockReason`: fed by
// events, a formula, pending review, not yours to edit, a future period, the wrong view for its
// cadence…). A cell with a value shows a small clock on hover that opens its history (as do its
// context menu and `H`). `CorrectionPopover` asks for the reason before a past value — or, for a
// seat's holder, any value — is replaced: old → new, a required reason (the observation's
// `note`), Save / Cancel; it is anchored to the cell like the history.
import {
  Button,
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  ScoreCell,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@companyos/ui";
import { HistoryIcon } from "lucide-react";
import {
  type KeyboardEventHandler,
  type MouseEvent as ReactMouseEvent,
  type Ref,
  useId,
  useState,
} from "react";
import { type GridCell, MIN_REASON, reasonValid } from "./scorecard-model";

export function GridScoreCell({
  cell,
  inFlight,
  reason,
  onEdit,
  onHistory,
  onKeyDown,
  cellRef,
  name,
  coords,
}: {
  cell: GridCell;
  /** A value on its way to the API (shown at half opacity). */
  inFlight: string | undefined;
  /** Why the cell takes no number, or null when it is editable. */
  reason: string | null;
  onEdit: () => void;
  /** Opens the cell's history (absent: the cell has no history to show). */
  onHistory: (() => void) | null;
  onKeyDown: KeyboardEventHandler<HTMLElement>;
  cellRef: Ref<HTMLElement>;
  /** "Placements, W42" (the history button's name). */
  name: string;
  /** The keyboard walk's coordinates (`data-r`, `data-c`). */
  coords: { r: number; c: number };
}) {
  const shared = {
    ref: cellRef,
    state: inFlight === undefined ? cell.state : ("none" as const),
    value: inFlight ?? cell.value,
    label: inFlight === undefined ? cell.label : `Saving ${inFlight}`,
    pending: inFlight !== undefined,
    onKeyDown,
    onContextMenu: onHistory
      ? (e: ReactMouseEvent) => {
          e.preventDefault();
          onHistory();
        }
      : undefined,
    "data-cell": "",
    "data-r": coords.r,
    "data-c": coords.c,
  };
  return (
    <>
      {reason === null ? (
        <ScoreCell {...shared} onEdit={onEdit} />
      ) : (
        // The trigger is a wrapper, so the cell keeps its own `data-slot="score-cell"`; focus
        // on the cell (the arrow keys reach it) bubbles to the trigger and shows the reason.
        <Tooltip>
          <TooltipTrigger render={<span className="block" />}>
            <ScoreCell
              {...shared}
              tabIndex={-1}
              title={undefined}
              aria-description={reason}
              data-reason={reason}
              className="focus:outline-2 focus:outline-solid focus:-outline-offset-2 focus:outline-ring"
            />
          </TooltipTrigger>
          <TooltipContent>{reason}</TooltipContent>
        </Tooltip>
      )}
      {onHistory ? (
        <button
          type="button"
          tabIndex={-1}
          aria-label={`History of ${name}`}
          data-slot="cell-history-button"
          onClick={onHistory}
          className="absolute top-0.5 left-0.5 z-[1] flex size-3.5 items-center justify-center rounded-xs text-muted-foreground opacity-0 transition-opacity group-hover/td:opacity-100 group-focus-within/td:opacity-100 hover:text-foreground pointer-coarse:opacity-100"
        >
          <HistoryIcon aria-hidden="true" className="size-2.5" />
        </button>
      ) : null}
    </>
  );
}

export function CorrectionPopover({
  anchor,
  title,
  from,
  to,
  saving,
  onSave,
  onCancel,
}: {
  anchor: Element | null;
  /** "Placements · W40" */
  title: string;
  /** The live value and the new one, formatted. */
  from: string;
  to: string;
  saving: boolean;
  onSave: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");
  const [tried, setTried] = useState(false);
  const id = useId();
  const ok = reasonValid(reason);
  const save = () => {
    setTried(true);
    if (ok) onSave(reason.trim());
  };
  return (
    <Popover open onOpenChange={(open) => (open ? null : onCancel())}>
      <PopoverContent
        anchor={anchor}
        align="start"
        className="w-80 gap-2 p-3"
        data-slot="correction-reason"
      >
        <PopoverHeader>
          <PopoverTitle>Correct {title}</PopoverTitle>
          <PopoverDescription>
            <span className="tabular-nums line-through">{from}</span>
            {" → "}
            <span className="font-medium text-foreground tabular-nums">{to}</span>
          </PopoverDescription>
        </PopoverHeader>
        <label htmlFor={id} className="text-xs font-medium text-foreground">
          Reason for the change
        </label>
        <Textarea
          id={id}
          autoFocus
          rows={3}
          value={reason}
          aria-invalid={tried && !ok ? true : undefined}
          aria-describedby={`${id}-hint`}
          placeholder="e.g. late invoice counted in the wrong week"
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              save();
            }
          }}
        />
        <p
          id={`${id}-hint`}
          className={`text-xs ${tried && !ok ? "text-danger-text" : "text-muted-foreground"}`}
        >
          {tried && !ok
            ? `Say why in at least ${MIN_REASON} characters.`
            : "Kept with the number's history."}
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={saving}>
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
